/* ============================================================
   StudyAK Instructor Panel — design tokens (ThemeProvider)
   ------------------------------------------------------------
   Every value here is extracted verbatim from the reference
   "StudyAK Instructor Panel v2" standalone HTML — the :root token
   block and the [data-*] tweak blocks (theme / accent / density /
   list / sidebar / rail / bg / btn / cards / font).

   `buildTheme(tweaks)` resolves a full styled-components theme for
   any combination of those tweaks, so the live Tweaks panel can
   re-skin the whole panel exactly like the reference's data-attribute
   driven CSS. No value is approximated; color-mix()/oklch()
   expressions are reproduced literally so output is pixel-identical.

   Reference default config:
     theme=light · accent=indigo · density=compact · list=comfortable
     sidebar=labeled · rail=slate · bg=mint · btn=ink
     cards=elevated · chart=donut · font=hanken
   ============================================================ */

// ---- font pairings (reference App controller FONT_MAP) ----
export const FONT_MAP = {
  hanken:   { body: '"Hanken Grotesk", system-ui, sans-serif', head: '"Hanken Grotesk", system-ui, sans-serif', label: 'Hanken Grotesk' },
  outfit:   { body: '"Outfit", system-ui, sans-serif', head: '"Outfit", system-ui, sans-serif', label: 'Outfit' },
  jakarta:  { body: '"Plus Jakarta Sans", system-ui, sans-serif', head: '"Plus Jakarta Sans", system-ui, sans-serif', label: 'Jakarta Sans' },
  sora:     { body: '"Plus Jakarta Sans", system-ui, sans-serif', head: '"Sora", system-ui, sans-serif', label: 'Sora + Jakarta' },
  franklin: { body: '"Libre Franklin", system-ui, sans-serif', head: '"Libre Franklin", system-ui, sans-serif', label: 'Libre Franklin' },
  grotesk:  { body: '"Libre Franklin", system-ui, sans-serif', head: '"Space Grotesk", system-ui, sans-serif', label: 'Grotesk + Franklin' },
};

// ---- the reference's resolved default tweak configuration ----
export const TWEAK_DEFAULTS = {
  theme: 'light',
  accent: 'indigo',
  density: 'compact',
  list: 'comfortable',
  sidebar: 'labeled',
  rail: 'slate',
  bg: 'mint',
  btn: 'ink',
  cards: 'elevated',
  chart: 'donut',
  font: 'hanken',
};

// ---- accent variants (per theme), with their --on-accent ----
const ACCENTS = {
  light: {
    indigo:  { accent: 'oklch(0.55 0.21 274)', strong: 'oklch(0.48 0.22 274)', on: '#fff' },
    violet:  { accent: 'oklch(0.56 0.25 305)', strong: 'oklch(0.49 0.26 305)', on: '#fff' },
    blue:    { accent: 'oklch(0.58 0.16 247)', strong: 'oklch(0.5 0.17 247)',  on: '#fff' },
    emerald: { accent: 'oklch(0.6 0.14 165)',  strong: 'oklch(0.52 0.14 165)', on: '#fff' },
    amber:   { accent: 'oklch(0.72 0.16 66)',  strong: 'oklch(0.64 0.16 66)',  on: 'oklch(0.25 0.05 66)' },
  },
  dark: {
    indigo:  { accent: 'oklch(0.68 0.17 274)', strong: 'oklch(0.74 0.16 274)', on: 'oklch(0.18 0.04 274)' },
    violet:  { accent: 'oklch(0.7 0.2 305)',   strong: 'oklch(0.76 0.19 305)', on: 'oklch(0.18 0.04 274)' },
    blue:    { accent: 'oklch(0.7 0.14 247)',  strong: 'oklch(0.76 0.13 247)', on: 'oklch(0.18 0.04 274)' },
    emerald: { accent: 'oklch(0.72 0.14 165)', strong: 'oklch(0.78 0.13 165)', on: '#fff' },
    amber:   { accent: 'oklch(0.78 0.15 66)',  strong: 'oklch(0.84 0.14 66)',  on: 'oklch(0.25 0.05 66)' },
  },
};

// ---- surfaces per theme ----
const SURFACES = {
  light: {
    bg: 'oklch(0.975 0.004 260)',
    surface: 'oklch(1 0 0)',
    surface2: 'oklch(0.985 0.004 260)',
    surface3: 'oklch(0.96 0.006 260)',
    border: 'oklch(0.915 0.006 260)',
    borderStrong: 'oklch(0.86 0.01 260)',
    text: 'oklch(0.26 0.02 268)',
    textMuted: 'oklch(0.52 0.018 268)',
    textSubtle: 'oklch(0.64 0.014 268)',
    ring1: '0 0 0 1px rgba(17,20,38,.07)',
    shadowSm: '0 1px 2px rgba(17,20,38,.06)',
    shadowMd: '0 1px 2px rgba(17,20,38,.04), 0 6px 16px -4px rgba(17,20,38,.10)',
    shadowLg: '0 24px 50px -16px rgba(17,20,38,.30), 0 8px 18px -8px rgba(17,20,38,.14)',
    softPct: 12, linePct: 30,
  },
  dark: {
    bg: 'oklch(0.17 0.015 268)',
    surface: 'oklch(0.215 0.018 268)',
    surface2: 'oklch(0.245 0.02 268)',
    surface3: 'oklch(0.29 0.022 268)',
    border: 'oklch(0.31 0.02 268)',
    borderStrong: 'oklch(0.4 0.025 268)',
    text: 'oklch(0.96 0.008 268)',
    textMuted: 'oklch(0.72 0.018 268)',
    textSubtle: 'oklch(0.6 0.018 268)',
    ring1: '0 0 0 1px rgba(255,255,255,.07)',
    shadowSm: '0 1px 2px rgba(0,0,0,.4)',
    shadowMd: '0 1px 2px rgba(0,0,0,.35), 0 8px 22px -6px rgba(0,0,0,.55)',
    shadowLg: '0 24px 54px -14px rgba(0,0,0,.72), 0 8px 18px -8px rgba(0,0,0,.5)',
    softPct: 20, linePct: 40,
  },
};

// ---- canvas background variants (per theme) ----
const BG = {
  light: { neutral: 'oklch(0.975 0.004 260)', white: 'oklch(0.992 0.002 260)', warm: 'oklch(0.972 0.013 72)', cool: 'oklch(0.97 0.013 240)', mint: 'oklch(0.972 0.015 162)', lilac: 'oklch(0.971 0.015 300)' },
  dark:  { neutral: 'oklch(0.17 0.015 268)', white: 'oklch(0.2 0.006 268)', warm: 'oklch(0.18 0.014 60)', cool: 'oklch(0.17 0.016 250)', mint: 'oklch(0.17 0.016 162)', lilac: 'oklch(0.172 0.018 300)' },
};

// ---- sidebar rail color (theme-independent — [data-rail] overrides both) ----
const RAILS = {
  slate:  { bg: 'oklch(0.21 0.012 250)', text: 'oklch(0.78 0.012 250)', strong: '#fff', hover: 'oklch(0.27 0.014 250)', border: 'oklch(0.3 0.014 250)' },
  ink:    { bg: 'oklch(0.155 0.003 270)', text: 'oklch(0.72 0.004 270)', strong: '#fff', hover: 'oklch(0.23 0.004 270)', border: 'oklch(0.26 0.004 270)' },
  plum:   { bg: 'oklch(0.2 0.05 305)', text: 'oklch(0.8 0.035 305)', strong: '#fff', hover: 'oklch(0.27 0.055 305)', border: 'oklch(0.3 0.055 305)' },
  ocean:  { bg: 'oklch(0.23 0.055 250)', text: 'oklch(0.82 0.035 250)', strong: '#fff', hover: 'oklch(0.3 0.06 250)', border: 'oklch(0.33 0.06 250)' },
  forest: { bg: 'oklch(0.22 0.045 162)', text: 'oklch(0.82 0.035 162)', strong: '#fff', hover: 'oklch(0.29 0.05 162)', border: 'oklch(0.32 0.05 162)' },
  light:  { bg: 'oklch(0.97 0.004 260)', text: 'oklch(0.48 0.018 260)', strong: 'oklch(0.24 0.02 260)', hover: 'oklch(0.93 0.007 260)', border: 'oklch(0.89 0.01 260)' },
};

// ---- primary button variants ----
const BTNS = {
  light: {
    ink:     { bg: 'oklch(0.22 0.008 270)', hover: 'oklch(0.3 0.01 270)', fg: '#fff' },
    blue:    { bg: 'oklch(0.52 0.15 250)', hover: 'oklch(0.46 0.16 250)', fg: '#fff' },
    emerald: { bg: 'oklch(0.54 0.12 162)', hover: 'oklch(0.48 0.12 162)', fg: '#fff' },
    amber:   { bg: 'oklch(0.72 0.15 66)', hover: 'oklch(0.66 0.15 66)', fg: 'oklch(0.24 0.05 66)' },
  },
  // dark only overrides ink; other variants reuse the light values.
  dark: {
    ink: { bg: 'oklch(0.95 0.005 270)', hover: '#fff', fg: 'oklch(0.2 0.01 270)' },
  },
};

// ---- density (default = comfortable) ----
const DENSITY = {
  comfortable: { space: '24px', gap: '16px', padCard: '22px', rowH: '52px', controlH: '40px', fsBase: '14.5px', fsSm: '12.5px', fsMicro: '11px' },
  compact:     { space: '16px', gap: '10px', padCard: '15px', rowH: '42px', controlH: '34px', fsBase: '13px', fsSm: '11.5px', fsMicro: '10px' },
};

// list rows are independent of overall density
const LIST_ROW = { comfortable: '54px', compact: '40px' };

const SEMANTIC = {
  success: 'oklch(0.62 0.15 158)',
  warning: 'oklch(0.74 0.15 72)',
  danger: 'oklch(0.6 0.21 25)',
  info: 'oklch(0.6 0.14 240)',
};

/**
 * Resolve a full styled-components theme object from a tweak config.
 * Unknown / missing keys fall back to the reference defaults.
 */
export function buildTheme(tweaks = {}) {
  const t = { ...TWEAK_DEFAULTS, ...tweaks };
  const mode = t.theme === 'dark' ? 'dark' : 'light';

  const sur = SURFACES[mode];
  const acc = (ACCENTS[mode][t.accent] || ACCENTS[mode].indigo);
  const rail = RAILS[t.rail] || RAILS.slate;
  const dens = DENSITY[t.density] || DENSITY.comfortable;
  const font = FONT_MAP[t.font] || FONT_MAP.hanken;
  const btn = (BTNS[mode] && BTNS[mode][t.btn]) || BTNS.light[t.btn] || BTNS.light.ink;

  const accent = acc.accent;
  const accentStrong = acc.strong;
  const onAccent = acc.on;

  // primary button: the "accent" variant tracks the accent token live
  const isAccentBtn = t.btn === 'accent';
  const btnBg = isAccentBtn ? accent : btn.bg;
  const btnBgHover = isAccentBtn ? accentStrong : btn.hover;
  const btnFg = isAccentBtn ? onAccent : btn.fg;

  const accentSoft = `color-mix(in oklch, ${accent} ${sur.softPct}%, ${sur.surface})`;
  const accentLine = `color-mix(in oklch, ${accent} ${sur.linePct}%, ${sur.border})`;

  // Pastel green fill for status badges — explicit light hex avoids oklch hue-wrap (pink tint).
  const successSoft = mode === 'dark'
    ? `color-mix(in srgb, ${SEMANTIC.success} 16%, ${sur.surface})`
    : '#ecfdf5';

  const cardShadow = t.cards === 'flat' ? 'none' : `${sur.ring1}, ${sur.shadowMd}`;
  const cardBorder = t.cards === 'flat' ? `1px solid ${sur.border}` : '1px solid transparent';

  // list density overrides the density's own rowH
  const rowH = LIST_ROW[t.list] || dens.rowH;

  return {
    // raw tweak echo (handy for components that branch on chart style, etc.)
    tweaks: t,
    mode,
    colorScheme: mode,

    // fonts
    fontBody: font.body,
    fontHead: font.head,

    // radii
    rSm: '8px', rMd: '12px', rLg: '16px', rXl: '22px', rPill: '999px',

    // density + list
    space: dens.space,
    gap: dens.gap,
    padCard: dens.padCard,
    rowH,
    controlH: dens.controlH,
    fsBase: dens.fsBase,
    fsSm: dens.fsSm,
    fsMicro: dens.fsMicro,
    sidebarW: t.sidebar === 'icon' ? '76px' : '248px',

    // surfaces
    bg: BG[mode][t.bg] || sur.bg,
    surface: sur.surface,
    surface2: sur.surface2,
    surface3: sur.surface3,
    border: sur.border,
    borderStrong: sur.borderStrong,
    text: sur.text,
    textMuted: sur.textMuted,
    textSubtle: sur.textSubtle,

    // accent
    accent,
    accentStrong,
    accentFg: '#fff',
    onAccent,
    accentSoft,
    accentLine,

    // semantic + chart palette
    ...SEMANTIC,
    successSoft,
    c1: accent,
    c2: 'oklch(0.7 0.13 200)',
    c3: 'oklch(0.74 0.15 72)',
    c4: 'oklch(0.62 0.15 158)',

    // primary button
    btnBg, btnBgHover, btnFg,

    // shadows / ring
    ring1: sur.ring1,
    shadowSm: sur.shadowSm,
    shadowMd: sur.shadowMd,
    shadowLg: sur.shadowLg,

    // sidebar rail
    railBg: rail.bg,
    railText: rail.text,
    railTextStrong: rail.strong,
    railHover: rail.hover,
    railBorder: rail.border,

    // card style
    cardShadow,
    cardBorder,
  };
}

// Back-compat named exports (callers that imported the old static themes).
export const lightTheme = buildTheme({ theme: 'light' });
export const darkTheme = buildTheme({ theme: 'dark' });

// Default export = the visible default config (light).
const theme = lightTheme;
export default theme;
